import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { Api } from 'src/app/shared/constants/api';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';

export interface ICsrRemarkDialogData {
  detailId: number;       // null = unsaved row
  isFormDisabled: boolean;
  /** Previously added pending remarks for this unsaved row — shown as read-only on re-open. */
  pendingRemarkDetails?: any[];
}

export interface ICsrRemarkDialogResult {
  /** Count of total saved remarks (populated when detailId existed and API call succeeded). */
  count?: number;
  /** Full remark detail objects for an unsaved row — parent posts them after saving the main record. */
  pendingDetails?: any[];
}

const REF_TYPE = 'CUSTOMER_SERVICE_REQUIREMENT_DETAIL';
const PARENT_REF_TYPE = 'CUSTOMER_SERVICE_REQUIREMENT';

@Component({
    selector: 'app-csr-remark-dialog',
    templateUrl: './csr-remark-dialog.component.html',
    styleUrls: ['./csr-remark-dialog.component.less'],
    standalone: false
})
export class CsrRemarkDialogComponent implements OnInit {

  /**
   * Formats a Date to a local 'YYYY-MM-DDTHH:mm:ss' string (no timezone), so the backend
   * LocalDateTime stores the exact time the remark was created without any UTC drift.
   */
  private static toLocalDateTimeString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  form: FormGroup;
  loading = false;
  saving = false;
  today = new Date().toISOString();

  loggedInUser: any = null;
  existingRemarksId: number = null;

  displayedColumns = ['remarksText', 'remarksDate', 'remarkByUserName', 'action'];
  remarksDataSource = new BehaviorSubject<AbstractControl[]>([]);

  constructor(
    private fb: FormBuilder,
    private service: DataService,
    private utilityService: UtilityService,
    public dialogRef: MatDialogRef<CsrRemarkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICsrRemarkDialogData
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      remarkDetails: this.fb.array([])
    });
    this.getLoggedInUser();
    if (this.data.detailId) {
      this.loadRemarks();
    } else if (this.data.pendingRemarkDetails?.length) {
      // Unsaved row — pre-populate with remarks already added in previous dialog opens (read-only)
      this.data.pendingRemarkDetails.forEach(d =>
        this.remarkDetails.push(this.buildRemarkRow(d, false))
      );
      this.remarksDataSource.next(this.remarkDetails.controls);
    }
  }

  get remarkDetails(): FormArray {
    return this.form.get('remarkDetails') as FormArray;
  }

  private getLoggedInUser() {
    this.service.get(Api.LOGGED_IN_USER).subscribe(res => {
      if (res) {
        this.loggedInUser = res;
      }
    });
  }

  private buildRemarkRow(data?: any, toSend = false): FormGroup {
    const isNew = !data;
    return this.fb.group({
      id: [data?.id || null],
      remarksText: [data?.remarksText || null, [Validators.required, Validators.maxLength(2000)]],
      remarksDate: [data?.remarksDate || this.today, [Validators.required]],
      remarkByUserName: [data?.remarkByUserName || (isNew ? this.loggedInUser?.name : null)],
      remarkByUserId: [data?.remarkByUserId || (isNew ? this.loggedInUser?.id : null)],
      isInternalRemark: [true],
      toSend: [toSend],
      expand: [false]
    });
  }

  private loadRemarks() {
    this.loading = true;
    this.service.get(`${Api.REMARKS}/reference/${REF_TYPE}/${this.data.detailId}`)
      .subscribe(res => {
        this.loading = false;
        if (res) {
          this.existingRemarksId = res.id;
          const details: any[] = res.remarkDetails || [];
          details.sort((a, b) => (b.id || 0) - (a.id || 0));
          details.forEach(d => this.remarkDetails.push(this.buildRemarkRow(d, false)));
          this.remarksDataSource.next(this.remarkDetails.controls);
        }
      }, () => { this.loading = false; });
  }

  addRemark() {
    this.remarkDetails.insert(0, this.buildRemarkRow(null, true));
    this.remarksDataSource.next(this.remarkDetails.controls);
  }

  removeRemark(index: number) {
    const row = this.remarkDetails.at(index);
    if (!row.get('toSend').value) return;
    this.remarkDetails.removeAt(index);
    this.remarksDataSource.next(this.remarkDetails.controls);
  }

  toggleExpand(element: AbstractControl) {
    element.get('expand').setValue(!element.get('expand').value);
    this.remarksDataSource.next(this.remarksDataSource.value);
  }

  save() {
    const toSendRows = this.remarkDetails.controls.filter(c => c.get('toSend').value);

    if (toSendRows.length === 0) {
      this.dialogRef.close(null);
      return;
    }

    toSendRows.forEach(c => c.get('remarksText').markAsTouched());
    if (toSendRows.some(c => c.get('remarksText').invalid)) return;

    // ── Unsaved row: return full detail objects to parent for later posting ──
    if (!this.data.detailId) {
      const pendingDetails = toSendRows
        .map(c => ({
          remarksText: (c.get('remarksText').value || '').trim(),
          remarksDate: c.get('remarksDate').value,
          remarkByUserId: c.get('remarkByUserId').value || this.loggedInUser?.id,
          remarkByUserName: c.get('remarkByUserName').value || this.loggedInUser?.name,
          isInternalRemark: true
        }))
        .filter(d => !!d.remarksText);
      const result: ICsrRemarkDialogResult = { pendingDetails };
      this.dialogRef.close(result);
      return;
    }

    // ── Saved row: POST to remarks API immediately ──
    this.saving = true;
    const body = this.buildRemarksBody(toSendRows);
    this.service.post(Api.REMARKS, body).subscribe(res => {
      this.saving = false;
      if (res) {
        this.utilityService.notifier('Remarks saved successfully', 'success');
        const result: ICsrRemarkDialogResult = { count: res.remarkDetails?.length ?? this.remarkDetails.controls.length };
        this.dialogRef.close(result);
      }
    }, () => {
      this.saving = false;
      this.utilityService.notifier('Failed to save remarks', 'error');
    });
  }

  private buildRemarksBody(toSendRows: AbstractControl[]): any {
    return {
      id: this.existingRemarksId,
      referenceType: REF_TYPE,
      parentReferenceId: this.data.detailId,
      parentPageRefType: PARENT_REF_TYPE,
      remarkDetails: toSendRows.map(c => {
        const raw = (c as FormGroup).getRawValue();
        return {
          ...raw,
          remarksText: (raw.remarksText || '').trim(),
          remarkByUserId: raw.remarkByUserId || this.loggedInUser?.id,
          remarkByUserName: raw.remarkByUserName || this.loggedInUser?.name
        };
      })
    };
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
